import React, { useState } from "react";
import { NavLink, useRouteMatch } from "polyrouter";
import { Layout } from "views/Main/Layouts";
import { useTranslation } from "utilities/i18n";
import { Card, Field, makeColumnContentForCard } from "collections/Cards";
import { Section } from "collections/Section";
import { SortState } from "store/filters";
import {
  sortTableData,
  TableBody,
  TableBulkItemRow,
  TableHeaderItem
} from "collections/Table";
import {
  License,
  LicenseAggregations,
  LicenseType,
  useLicenseDetailsQuery
} from "generated/graphql";
import { LoadingIndicator, NoResultsIndicator } from "elements";
import { formatDate } from "routes/Manage/Inventory/View/DeviceDetails";
import { useTenantId } from "polyrouter/SelectedTenantProvider";

type LicenseInfo = Omit<License, "entitlements" | "details">;

const LicenseKeyInformation: React.FC<{
  license: LicenseInfo;
}> = ({ license }) => {
  const { t } = useTranslation();

  const fields: Field[] = [
    {
      key: "licenseType",
      label: t("TYPE"),
      content: <p>{t(license.type)}</p>
    },
    {
      key: "activationDate",
      label: t("DATE_ADDED"),
      content: <p>{formatDate(license.activationDate)}</p>
    },
    {
      key: "endDate",
      label: t("EXPIRATION"),
      content: <p>{formatDate(license.endDate)}</p>
    },
    {
      key: "quantity",
      label: t("QUANTITY"),
      content: <p>{license.quantity}</p>
    },
    {
      key: "userId",
      label: t("ACTIVATED_BY"),
      content: <p>{license.userId}</p>
    }
  ];

  return (
    <Section>
      <Section.Header>{t("LICENSE_KEY_INFORMATION")}</Section.Header>
      <Card className="table-card full-width">
        {makeColumnContentForCard(fields, 3)}
      </Card>
    </Section>
  );
};

const EntitlementsTable: React.FC<{
  aggregations: LicenseAggregations[];
  type: LicenseType;
}> = ({ aggregations, type }) => {
  const { t } = useTranslation();
  const [currentSort, setSort] = useState<SortState>({
    field: "name",
    direction: "DESC"
  });

  const tableData = aggregations.map(
    ({ name, activeLicenseCount, entitledDeviceCount }) => {
      return {
        name,
        activeLicenseCount,
        assignedLicenseCount: entitledDeviceCount,
        availableLicenseCount: activeLicenseCount - entitledDeviceCount
      };
    }
  );
  sortTableData(tableData, currentSort);

  const isClaritiLicense = type === LicenseType.Clariti;
  const columns = isClaritiLicense
    ? [
        { key: "name", label: t("NAME") },
        { key: "totalLicenseCount", label: t("QUANTITY") }
      ]
    : [
        { key: "name", label: t("DEVICE_MODEL") },
        { key: "totalLicenseCount", label: t("QUANTITY") },
        { key: "assignedLicenseCount", label: t("ASSIGNED") },
        { key: "availableLicenseCount", label: t("AVAILABLE") }
      ];

  return (
    <>
      <Section>
        <Section.Header>
          {t("ENTITLEMENT_COUNT", { count: tableData.length })}
        </Section.Header>
      </Section>
      <div className="table-container">
        <table className="table custom-table">
          <thead>
            <tr>
              {columns.map(({ key, label }) => (
                <TableHeaderItem
                  fieldName={key}
                  title={label}
                  sort={{ currentSort, onSort: setSort }}
                  key={key}
                />
              ))}
            </tr>
          </thead>

          <TableBody loading={false} cols={columns.length - 1}>
            {tableData.map((rowData, i) => {
              return (
                <TableBulkItemRow
                  key={`row-${i}`}
                  showChecks={false}
                  onCheck={() => {}}
                  checked={false}
                >
                  {isClaritiLicense ? (
                    <>
                      <td>{rowData.name}</td>
                      <td>{rowData.activeLicenseCount}</td>
                    </>
                  ) : (
                    <>
                      <td>{rowData.name}</td>
                      <td>{rowData.activeLicenseCount}</td>
                      <td>{rowData.assignedLicenseCount}</td>
                      <td>{rowData.availableLicenseCount}</td>
                    </>
                  )}
                </TableBulkItemRow>
              );
            })}
          </TableBody>
        </table>
      </div>
    </>
  );
};

export const LicenseDetails: React.FC = () => {
  const { t } = useTranslation();
  const tenantId = useTenantId();
  const { licenseKey } = useRouteMatch<{ licenseKey: string }>().params;
  const { data, fetching } = useLicenseDetailsQuery({
    variables: { tenantId, licenseKey }
  });
  const isPolyPlus = !!useRouteMatch("/manage/licensing/poly-plus");
  const isClariti = !!useRouteMatch("/manage/licensing/clariti");

  if (fetching) {
    return <LoadingIndicator />;
  }
  if (!data) {
    return <NoResultsIndicator />;
  }

  const { Sidebar, Content } = Layout;
  return (
    <>
      <Sidebar>
        <span className="title-capitalized">
          {isPolyPlus
            ? t("POLY_PLUS")
            : isClariti
            ? t("CLARITI")
            : t("LICENSING")}
        </span>
        <ul className="menu router">
          <li>
            <NavLink nest to="">
              {t("OVERVIEW")}
            </NavLink>
          </li>
        </ul>
      </Sidebar>
      <Content className="license-details" data-testid="license-details">
        <Content.Title>
          {t("LICENSE_KEY")} <p>| {licenseKey}</p>
        </Content.Title>
        <LicenseKeyInformation license={data.license as LicenseInfo} />
        <EntitlementsTable
          aggregations={data.licenseAggregationsByModel}
          type={data.license.type}
        />
      </Content>
    </>
  );
};
